import type {Praeparat} from '@/types/otc/praeparat';
import type {Product} from '@/types/otc/product';
import type {OtcKategorie} from '@/types/otc/otcKategorie';
import type {Dokument} from '@/types/otc/dokument';
import type {Artikel} from '@/types/otc/artikel';

/**
 * Normalisiert die Properties von API-Responses zu einem einheitlichen Format
 * Konvertiert sowohl camelCase als auch PascalCase zu einem einheitlichen Format
 */
export class DataTransformer {

    /**
     * Normalisiert ein Product-Objekt von verschiedenen API-Quellen
     * @param data - Roh-Daten vom API (kann camelCase oder PascalCase sein)
     * @returns Normalisiertes Product-Objekt
     */
    static normalizeProduct(data: any): Product {
        if (!data) return data;

        return {
            id: data.id ?? data.Id ?? 0,
            name: data.name ?? data.Name ?? '',
            praeparate: data.praeperate ? this.normalizePraeparatArray(data.praeperate) :
                        data.Praeperate ? this.normalizePraeparatArray(data.Praeperate) :[]
        };
    }

    /**
     * Normalisiert ein Praeparat-Objekt von verschiedenen API-Quellen
     * @param data - Roh-Daten vom API (kann camelCase oder PascalCase sein)
     * @returns Normalisiertes Praeparat-Objekt
     */
    static normalizePraeparat(data: any): Praeparat {
        if (!data) return data;
        return {
            id: data.id ?? data.Id ?? 0,
            name: data.name ?? data.Name ?? '',
            anwendungsgebiet: data.anwendungsgebiet ?? data.Anwendungsgebiet ?? null,
            anwendungsgebietKurz: data.anwendungsgebietKurz ?? data.AnwendungsgebietKurz ?? null,
            anwendungshinweise: data.anwendungshinweise ?? data.Anwendungshinweise ?? null,
            artikel: data.artikel ? this.normalizeArtikelArray(data.artikel) :
                data.Artikel ? this.normalizeArtikelArray(data.Artikel) : [],
            dokumente: data.dokumente ? this.normalizeDokumentArray(data.dokumente) :
                data.Dokumente ? this.normalizeDokumentArray(data.Dokumente) : [],

            bemerkungen: data.bemerkungen ?? data.Bemerkungen ?? null,
            bildKey: data.bildKey ?? data.BildKey ?? null,
            bruchkerbe: data.bruchkerbe ?? data.Bruchkerbe ?? null,
            darreichungsform: data.darreichungsform ?? data.Darreichungsform ?? null,
            faq: data.faq ?? data.Faq ?? null,
            formUndFarbe: data.formUndFarbe ?? data.FormUndFarbe ?? null,
            gebrauchsInfoKey: data.gebrauchsInfoKey ?? data.GebrauchsInfoKey ?? null,

            hatRabattvertraege: data.hatRabattvertraege ?? data.HatRabattvertraege ?? false,
            laktosefrei: data.laktosefrei ?? data.Laktosefrei ?? false,
            
            otcKategorieId: data.otcKategorieId ?? data.otcKategorieId ?? data.OtcKategorieId ?? "",
            otcKategorie: data.otcKategorie ? this.normalizeOtcKategorie(data.otcKategorie) :
                data.OtcKategorie ? this.normalizeOtcKategorie(data.OtcKategorie) : null,

            patenthinweise: data.patenthinweise ?? data.Patenthinweise ?? null,
            pflichttext: data.pflichttext ?? data.Pflichttext ?? null,
            produktId: data.produktId ?? data.ProduktId ?? 0,
            produkthinweise: data.produkthinweise ?? data.Produkthinweise ?? null,

            sondengaengigkeitNichtZerkleinert: data.sondengaengigkeitNichtZerkleinert ?? data.SondengaengigkeitNichtZerkleinert ?? null,
            sondengaengigkeitZerkleinert: data.sondengaengigkeitZerkleinert ?? data.SondengaengigkeitZerkleinert ?? null,

            suspendierbarkeitNichtZerkleinert: data.suspendierbarkeitNichtZerkleinert ?? data.SuspendierbarkeitNichtZerkleinert ?? null,
            suspendierbarkeitZerkleinert: data.suspendierbarkeitZerkleinert ?? data.SuspendierbarkeitZerkleinert ?? null,

            teilbarkeit: data.teilbarkeit ?? data.Teilbarkeit ?? null,
            wirkstoff: data.wirkstoff ?? data.Wirkstoff ?? null,
            wirkstoffInhalt: data.wirkstoffInhalt ?? data.WirkstoffInhalt ?? null,
            zerkleinerbarkeit: data.zerkleinerbarkeit ?? data.Zerkleinerbarkeit ?? null,
            zusammensetzung: data.zusammensetzung ?? data.Zusammensetzung ?? null,
            url: data.url ?? data.Url ?? null
        };
    }

    /**
     * Normalisiert ein OtcKategorie-Objekt
     * @param data - Roh-Daten der OTC-Kategorie
     */
    static normalizeOtcKategorie(data: any): OtcKategorie {
        if (!data) return data;

        return {
            id: data.id ?? data.Id ?? 0,
            name: data.name ?? data.Name ?? '',
            farbe: data.farbe ?? data.Farbe ?? '',
            fond: data.fond ?? data.Fond ?? '',
        };
    }

    /**
     * Normalisiert ein Dokument-Objekt
     * @param data - Roh-Daten des Dokuments
     */
    static normalizeDokument(data: any): Dokument {
        if (!data) return data;

        return {
            name: data.name ?? data.Name ?? '',
            titel: data.titel ?? data.Titel ?? null,
            beschreibung: data.beschreibung ?? data.Beschreibung ?? null,
            dateiKey: data.dateiKey ?? data.DateiKey ?? '',
            dateiUrl: data.dateiUrl ?? data.DateiUrl ?? null,
            isAllgDokument: data.isAllgDokument ?? data.IsAllgDokument ?? false,
            isRabattFile: data.isRabattFile ?? data.IsRabattFile ?? false,
        };
    }

    /**
     * Normalisiert ein Artikel-Objekt
     * @param data - Roh-Daten des Artikels
     */
    static normalizeArtikel(data: any): Artikel {
        if (!data) return data;

        return {
            avp: data.avp ?? data.Avp ?? data.AVP ?? '',
            bildKey: data.bildKey ?? data.BildKey ?? null,
            excludedOnlineApos: data.excludedOnlineApos ?? data.ExcludedOnlineApos ?? null,
            isOtc: data.isOtc ?? data.IsOtc ?? data.isOTC ?? data.IsOTC ?? false,
            isRezeptpflichtig: data.isRezeptpflichtig ?? data.IsRezeptpflichtig ?? false,
            isZuzahlungsbefreit: data.isZuzahlungsbefreit ?? data.IsZuzahlungsbefreit ?? false,
            name: data.name ?? data.Name ?? '',
            packung: data.packung ?? data.Packung ?? '',
            praeparatId: data.praeparatId ?? data.PraeparatId ?? 0,
            praeparatName: data.praeparatName ?? data.PraeparatName ?? '',
            pzn: data.pzn ?? data.Pzn ?? data.PZN ?? '',
            showInApoFinder: data.showInApoFinder ?? data.ShowInApoFinder ?? false,
            showInOnlineApo: data.showInOnlineApo ?? data.ShowInOnlineApo ?? false,
        };
    }

    /**
     * Normalisiert ein Array von Product-Objekten
     * @param dataArray - Array von Roh-Daten
     */
    static normalizeProductArray(dataArray: any[]): Product[] {
        if (!Array.isArray(dataArray)) return [];
        return dataArray.map(item => this.normalizeProduct(item));
    }

    /**
     * Normalisiert ein Array von Praeparat-Objekten
     * @param dataArray - Array von Roh-Daten
     */
    static normalizePraeparatArray(dataArray: any[]): Praeparat[] {
        if (!Array.isArray(dataArray)) return [];
        return dataArray.map(item => this.normalizePraeparat(item));
    }

    /**
     * Normalisiert ein Array von OtcKategorie-Objekten
     * @param dataArray - Array von Roh-Daten
     */
    static normalizeOtcKategorieArray(dataArray: any[]): OtcKategorie[] {
        if (!Array.isArray(dataArray)) return [];
        return dataArray.map(item => this.normalizeOtcKategorie(item));
    }

    /**
     * Normalisiert ein Array von Dokument-Objekten
     * @param dataArray - Array von Roh-Daten
     */
    static normalizeDokumentArray(dataArray: any[]): Dokument[] {
        if (!Array.isArray(dataArray)) return [];
        return dataArray.map(item => this.normalizeDokument(item));
    }

    /**
     * Normalisiert ein Array von Artikel-Objekten
     * @param dataArray - Array von Roh-Daten
     */
    static normalizeArtikelArray(data: any): Artikel[] {
        if(data && data.length>0){
            if(Array.isArray(data)) return data.map(item => this.normalizeArtikel(item));
            const dataArray = JSON.parse(data);
            if(dataArray && Array.isArray(dataArray)) return dataArray.map(item => this.normalizeArtikel(item));
        }
        return [] as Artikel[];
        
    }


    private static getChangedProperties(original: any, normalized: any): string[] {
        const changes: string[] = [];

        for (const key in normalized) {
            if (original[key] !== normalized[key]) {
                // Prüfe ob der Wert aus einer alternativen Property kam
                const alternativeKey = this.getAlternativeKey(key);
                if (alternativeKey && original[alternativeKey] === normalized[key]) {
                    changes.push(`${alternativeKey} → ${key}`);
                }
            }
        }

        return changes;
    }

    /**
     * Ermittelt die alternative Schreibweise einer Property
     */
    private static getAlternativeKey(key: string): string | null {
        // camelCase → PascalCase
        if (key.charAt(0) === key.charAt(0).toLowerCase()) {
            return key.charAt(0).toUpperCase() + key.slice(1);
        }
        // PascalCase → camelCase
        if (key.charAt(0) === key.charAt(0).toUpperCase()) {
            return key.charAt(0).toLowerCase() + key.slice(1);
        }
        return null;
    }

    /**
     * Generische Normalisierung für beliebige Objekte
     * Falls ihr noch ander Datenstrukturen habt.
     */
    static normalizeGeneric<T>(data: any, propertyMap: Record<string, string>): T {
        if (!data) return data;

        const normalized: any = {};

        for (const [targetKey, sourceKeys] of Object.entries(propertyMap)) {
            const sources = sourceKeys.split('|'); // z.B. "name|Name"
            for (const sourceKey of sources) {
                if (data[sourceKey] !== undefined) {
                    normalized[targetKey] = data[sourceKey];
                    break;
                }
            }
        }

        return normalized as T;
    }
}


export const {
    normalizeProduct,
    normalizeProductArray,
    normalizePraeparat,
    normalizePraeparatArray,
    normalizeOtcKategorie,
    normalizeOtcKategorieArray,
    normalizeDokument,
    normalizeDokumentArray,
    normalizeArtikel,
    normalizeArtikelArray,
} = DataTransformer;